import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-premium';
import React, { useMemo } from 'react';
import { ENTITY_CONSTANTS } from '../../../constants/entityConstants';
import { FEATURE_CONSTANTS } from '../../../constants/featureConstants';
import { useI18n } from '../../../i18n/I18n';
import DataGrid from '../../../list/DataGrid';
import { useInfiniteLoad } from '../../../list/useInfiniteLoad';
import { getEngagementListViewConfiguration } from './EngagementDataList';

interface EngagementTrackerDataGridProps {
    /**
     * Engagement tracker data, expected to have a nested structure with results, hits, and items.
     */
    data?: {
        results?: {
            hits?: {
                items?: Array<GridValidRowModel & { id?: string | number }>;
            };
        };
    };
    /**
     * Loading state for the engagement tracker data.
     */
    loading?: boolean;
    /**
     * Page size for infinite loading, passed from parent ListView.
     */
    pageSize?: number;
}

const COLUMNS: GridColDef[] = [
    {
        field: 'icon',
        resizable: false,
        disableReorder: true,
        sortable: false
    },
    {
        field: 'description',
        flex: 1,
        sortable: false
    },
    {
        field: 'date',
        width: 110,
        sortable: false,
        align: 'right',
        headerAlign: 'right'
    }
];

function EngagementTrackerDataGrid({ data, loading = false, pageSize }: EngagementTrackerDataGridProps) {
    const t = useI18n();

    const allRows = useMemo(() => {
        const items = data?.results?.hits?.items ?? [];
        // Filter out notes without context
        return items.filter((item) => {
            if (item.baseEntity?.type === ENTITY_CONSTANTS.note && !item.contextTerm) {
                return false;
            }
            return true;
        });
    }, [data?.results?.hits?.items]);

    const { displayedRows, loadMore } = useInfiniteLoad({
        allRows,
        pageSize,
        isLoading: loading
    });

    const listViewConfiguration = getEngagementListViewConfiguration();

    return (
        <DataGrid
            id="engagement-tracker"
            containerSx={{
                '& .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-virtualScroller': { gap: 0 },
                borderInline: 'none',
                '& .MuiIconButton-root': {
                    cursor: 'default'
                },
                '& .MuiIconButton-root:hover': {
                    backgroundColor: 'transparent'
                }
            }}
            feature={FEATURE_CONSTANTS.engagementTracker}
            rows={displayedRows}
            loading={loading}
            rowCount={allRows.length}
            columns={COLUMNS}
            includeActions={false}
            hideHeader
            asIsColumns={['icon']}
            listViewConfiguration={listViewConfiguration}
            addTitle={t('person.profile.engagement')}
            copyTooltipTitle={t('person.profile.engagement')}
            serverPagination={false}
            disablePagination
            scrollEndThreshold={50}
            onScrollEnd={loadMore}
        />
    );
}

export default EngagementTrackerDataGrid;
